import { useGuardedDefaultValues } from "@msuite/ui/hooks";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { responseHandler } from "@msuite/ui/utils";
import { type ResourceLinkFormSchema, resourceLinkSchema } from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { ResourceLinkForm } from "@/forms/resource-link-form";
import { client } from "@/hono";
import { ModalLoadFailed } from "@/components/modals";

/** Props Interface */
interface EditResourceLinkModalProps extends ModalProps {
	projectId: string;
	resourceLinkId: string;
	hideTicketPicker?: boolean;
	onComplete?: () => Promise<void> | void;
}

export function EditResourceLinkModal({
	isOpen,
	onClose,
	projectId,
	resourceLinkId,
	hideTicketPicker = false,
	onComplete,
}: EditResourceLinkModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { defaultValues, loadFailed } = useGuardedDefaultValues(async () => {
		const response = await client.projects[":projectId"]["resource-links"][":resourceLinkId"].$get({
			param: { projectId, resourceLinkId },
		});
		const data = await responseHandler(response);
		return resourceLinkSchema.parse(data.resourceLink);
	});
	const methods = useForm<ResourceLinkFormSchema>({
		resolver: resolver(resourceLinkSchema),
		defaultValues,
	});

	/** Functions */
	async function handleOnSubmit(json: ResourceLinkFormSchema) {
		try {
			const response = await client.projects[":projectId"]["resource-links"][
				":resourceLinkId"
			].$patch({
				param: { projectId, resourceLinkId },
				json,
			});
			await responseHandler(response);
			methods.reset();
			onClose();
			await onComplete?.();
		} catch (error) {
			console.error(error);
		}
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				title={t("resource_links.edit_resource_link")}
				width="500px"
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading && !loadFailed}
				data-cy="edit-resource-link-modal"
			>
				{loadFailed ? (
					<ModalLoadFailed onClose={onClose} />
				) : (
					<>
						<Modal.Body>
							<ResourceLinkForm
								projectId={projectId}
								hideTicketPicker={hideTicketPicker}
							/>
						</Modal.Body>
						<Modal.FooterForm />
					</>
				)}
			</Modal>
		</FormProvider>
	);
}
